'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { formatZipCode, formatPhoneNumber } from '@/components/customercenter-ui/utils/formatUtils';
import { useAccount } from '@/frontastic';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { getCookieValue } from '@/helpers/utils/siteUtils';
import { CustomerAddressPayload, SendCatalogPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import EditAddressModal from './editAddress';
import { COOKIE_KEYS, MAX_PAGE_SIZE } from '../../../../../constants/constant';
import { CustomFloatingSelect } from '../../customer-detail/components/customSelect';
import { PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE, TYPE_CATALOG } from '../../customer-detail/constants';
import { ShippingAddressDetailsType } from '../../customer-detail/types';
import { transformShippingAddress } from '../../customer-detail/utils';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { useToast } from '../../ui/use-toast';
import { maskPhoneNumber, maskPostalCode } from '../../utils/formatUtils';

const SendCatalogModal = ({
  Texts,
  divisionCustomerId,
  open,
  setOpen,
  billingAddress,
}: {
  Texts: any;
  divisionCustomerId: string;
  open: boolean;
  setOpen: any;
  billingAddress: ShippingAddressDetailsType;
}) => {
  const { toast } = useToast();
  const { sendCatalog, getCustomerAddresses } = useAccount();
  const params = useSearchParams();
  const [shippingAddresses, setShippingAddresses] = useState<ShippingAddressDetailsType[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<ShippingAddressDetailsType>();
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const getShippingAddress = async () => {
    setLoading(true);
    const payload = {
      query: {
        omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID),
        pageSize: MAX_PAGE_SIZE,
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as CustomerAddressPayload;
    await getCustomerAddresses(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        const transformedAddress = transformShippingAddress(response?.RecipientAddresses || []);
        setShippingAddresses([billingAddress, ...transformedAddress]);
        setSelectedAddress(transformedAddress[0] || billingAddress);
      }
    });
    setLoading(false);
  };
  useEffect(() => {
    getShippingAddress();
  }, []);

  const handleAddressChange = (id: string) => {
    const selectedAddress = shippingAddresses?.filter((items) => items?.addressId === id);
    setSelectedAddress(selectedAddress[0]);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const sessionEmail = sessionStorage.getItem('customerEmail');
    const address = {
      firstName: selectedAddress?.firstName,
      lastName: selectedAddress?.lastName,
      line1: selectedAddress?.streetName,
      ...(selectedAddress?.additionalAddressInfo && { line2: selectedAddress?.additionalAddressInfo }),
      city: selectedAddress?.city,
      state: selectedAddress?.state,
      zip: formatZipCode(selectedAddress?.postalCode ?? ''),
      country: selectedAddress?.country,
      ...(selectedAddress?.phone && { phone: formatPhoneNumber(selectedAddress?.phone) }),
      ...(sessionEmail && { emailAddress: sessionEmail }),
    };
    const catalogPayload = {
      query: {
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      },
      catalogPayload: {
        requestType: TYPE_CATALOG,
        customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID),
        omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID),
        divisionCustomerId: divisionCustomerId,
        Address: { ...address },
      },
    } as SendCatalogPayload;
    await sendCatalog(catalogPayload)
      .then(() => {
        setLoading(false);
        SuccessNotification(toast, { description: 'Catalog Sent', action: <SuccessIcon className="h-5 w-5" /> });
        setOpen(false);
      })
      .catch(() => {
        ErrorNotification(toast, { description: 'Error Sending Catalog', action: <ErrorIcon className="h-5 w-5" /> });
        setLoading(false);
      });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
      }}
    >
      <DialogContent className="max-w-[520px] bg-white sm:rounded-md">
        {loading && <Loader />}
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            <div className="text-2xl font-bold">{Texts.sendCatalog}</div>
          </DialogTitle>
        </DialogHeader>
        <section>
          <CustomFloatingSelect
            label={Texts.selectAddress}
            options={shippingAddresses?.map((items) => (
              <option key={items?.addressId} value={items?.addressId}>
                <p className="gbmask">{items?.fullName}</p>
              </option>
            ))}
            defaultValue={selectedAddress?.addressId}
            placeholder={Texts.selectAddress}
            onChange={(id) => handleAddressChange(id)}
            className="w-[400px]"
          />
          {selectedAddress && (
            <>
              <div className="gbmask mt-6">
                <p>{selectedAddress?.fullName}</p>
                <p>
                  {selectedAddress?.streetName} {selectedAddress?.city}
                </p>
                <p>
                  {selectedAddress?.state}, {maskPostalCode(selectedAddress?.postalCode ?? '')}
                </p>
                <p className="my-4">{maskPhoneNumber(selectedAddress?.phone ?? '')}</p>
              </div>

              <button className="text-blueLight underline" onClick={() => setOpenModal(true)}>
                {Texts.editAddress}
              </button>
            </>
          )}
          {openModal && (
            <EditAddressModal
              Texts={Texts}
              data={selectedAddress}
              setSelectedBillingAddress={setSelectedAddress}
              open={openModal}
              setOpen={setOpenModal}
              makeAPIcall={false}
            />
          )}
        </section>
        <section className="flex justify-start gap-4">
          <Button className="rounded-md bg-deepBlue p-4 text-white" onClick={handleSubmit} disabled={!selectedAddress}>
            {Texts.sendCatalog}
          </Button>
          <Button className="p-0 underline" onClick={() => setOpen(false)}>
            {Texts.cancelButton}
          </Button>
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default SendCatalogModal;
